/**
 * Home — the one screen that renders two entirely different products.
 *
 *  - `state.gameDay.active === false` → screen 3a, the planning home
 *  - `state.gameDay.active === true`  → screen 2a, the game-day card stack
 *
 * The game-day order is fixed (D30). A card with nothing to say is omitted
 * outright — there are no empty cards on this screen, ever.
 *
 * Colour rule that bites hardest here: `dim` on a `fill` ground measures
 * 1.33:1, so everything inside the filled score header is `ink` at a reduced
 * alpha. `Freshness` paints itself with `var(--dim)`, so the one place it sits
 * on fill gets a local `--dim` override derived from the theme's own ink.
 */

import { useCallback, useState, type CSSProperties } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { Challenge, ChallengeParticipant, FeedEvent, Game } from '@shared/types'
import { TEAMS_BY_ID, teamAbbr } from '@shared/teams'
import { useStore } from '../lib/store'
import { useTheme } from '../theme/ThemeProvider'
import { ApiFailure, POST, PUT, writeOrQueue } from '../lib/api'
import {
  Avatar, Body, Button, ButtonPair, Card, Divider, EmptyState, Eyebrow, Field, Freshness,
  Header, Notice, ProgressBar, Screen, SkeletonCard, Spacer,
} from '../ui'
import { AdSlot } from '../ui/AdSlot'
import { MatchupCard } from '../ui/MatchupCard'
import { SeasonStandings } from '../ui/SeasonStandings'
import { CheckInCard, gatheringLabel } from '../ui/CheckInCard'
import { FundCard, FundStrip } from '../ui/FundCard'
import { RootingTable } from '../ui/Rooting'
import { TimelineList } from '../ui/TimelineList'
import { ShoutSheet } from '../ui/Shout'

// ── formatting ──────────────────────────────────────────────────────────────

const WEEKDAY_SHORT = new Intl.DateTimeFormat(undefined, { weekday: 'short' })
const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })

/** "1:41" — the feed gutter and the score strip drop the meridiem. */
function bareTime(at: number): string {
  return HHMM.format(at).replace(/\s?[AP]\.?M\.?$/i, '')
}

/** "Sun 4:05" — list rows away from today. */
function shortDayTime(at: number): string {
  return `${WEEKDAY_SHORT.format(at)} ${bareTime(at)}`
}

function money(cents: number): string {
  const whole = Math.round(cents / 100)
  return `$${whole.toLocaleString()}`
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '??'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}

function periodLabel(period: number | null): string {
  if (!period) return ''
  if (period <= 4) return `Q${period}`
  return period === 5 ? 'OT' : `OT${period - 4}`
}

/** The right-hand column of an "around the league" row. */
function gameClock(g: Game): string {
  if (g.state === 'pre') return shortDayTime(g.kickoffAt)
  if (g.state === 'post') return g.wentToOt ? 'Final/OT' : 'Final'
  const parts = [periodLabel(g.period), g.clockDisplay].filter(Boolean)
  return parts.join(' ') || g.statusDetail || 'Live'
}

/** The spread reads from the home team's perspective; negative means home. */
function spreadLabel(g: Game): string | null {
  if (g.spread === null) return null
  if (g.spread === 0) return 'PK'
  const favorite = g.spread < 0 ? g.homeTeamId : g.awayTeamId
  return `${teamAbbr(favorite)} −${Math.abs(g.spread)}`
}

function sideNames(side: ChallengeParticipant[]): string {
  const names = side.map((p) => p.displayName).filter(Boolean)
  return names.length ? names.join(' & ') : 'nobody yet'
}

function otherSideName(c: Challenge): string {
  const other = c.viewer.side === 'a' ? c.sides.b : c.sides.a
  return sideNames(other)
}

function mySideName(c: Challenge): string {
  if (!c.viewer.isParticipant || !c.viewer.side) return sideNames(c.sides.a)
  return 'You'
}

// ── small shared pieces ─────────────────────────────────────────────────────

const CARD_GAP = 24

const monoRow: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 10,
  fontSize: 12,
  padding: '9px 2px',
  borderTop: '1px solid var(--line)',
}

/**
 * Home's own rules. Inline styles cannot express a media query or reach into a
 * shared component's markup, and styles.css is out of bounds for screens, so
 * they live here.
 *
 * ── the module headings ─────────────────────────────────────────────────────
 *
 * An eyebrow is 10px mono, uppercase, in `dim`. That is the right weight for a
 * label inside a card, where the card's own border already says "new section
 * starts here". Home has no such borders: it is eleven modules stacked down a
 * single column, and at 10px grey the only thing separating "Next games" from
 * the timeline above it was whitespace. They are headings, so they are sized
 * like headings — 14px, `fg`, bold, still mono and still uppercase so they
 * stay recognisably the same element as everywhere else in the app.
 *
 * Done by selector rather than by a prop because half of these modules are
 * shared components — the fund strip, the standings, the rooting table, the
 * check-in card — and they render on other screens too, where the quiet
 * version is still correct.
 */
const HOME_CSS = `
.sfg-home-cols .eyebrow,
.sfg-home-cols .eyebrow-lg {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--fg);
}

@media (min-width: 1025px) {
  .sfg-home-cols {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 28px;
    align-items: start;
    max-width: 1040px;
    margin: 0 auto;
  }
  .sfg-home-cols > * { min-width: 0; }
}
`

function HomeStyles() {
  return <style>{HOME_CSS}</style>
}

// ── entry point ─────────────────────────────────────────────────────────────

export default function Home() {
  const { state } = useStore()

  if (!state) {
    return (
      <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Header bordered={false} wordmark />
        <Body>
          <SkeletonCard lines={4} />
          <Spacer h={12} />
          <SkeletonCard lines={3} />
          <Spacer h={12} />
          <SkeletonCard lines={2} />
        </Body>
      </Screen>
    )
  }

  return state.gameDay.active ? <GameDayHome /> : <NormalHome />
}

// ════════════════════════════════════════════════════════════════════════════
// Game day (screen 2a)
// ════════════════════════════════════════════════════════════════════════════

function GameDayHome() {
  const { state, error } = useStore()
  const { theme } = useTheme()
  const navigate = useNavigate()
  if (!state) return null

  const me = state.user
  const myGame = state.myGame
  const league = state.games.filter((g) => g.id !== myGame?.id)
  const favorites = state.roomFavorites
  const fund = state.fund
  const live = state.challenges.live.slice(0, 2)
  const feed = state.feed
  const present = state.roster.present
  const overflow = state.roster.overflow

  const headcount = state.attendance?.headcount ?? present.length + overflow
  const nickname = TEAMS_BY_ID[me.teamId]?.nickname ?? teamAbbr(me.teamId)

  // `ink` at 78% — the only correct way to soften text on a filled ground.
  const inkSoft = `${theme.ink}C7`
  const inkFaint = `${theme.ink}CC`

  const us = myGame ? (myGame.homeTeamId === me.teamId ? myGame.homeScore : myGame.awayScore) : null
  const them = myGame ? (myGame.homeTeamId === me.teamId ? myGame.awayScore : myGame.homeScore) : null
  const oppId = myGame ? (myGame.homeTeamId === me.teamId ? myGame.awayTeamId : myGame.homeTeamId) : null
  const myWinPct = myGame && myGame.homeWinProb !== null
    ? Math.round(myGame.homeTeamId === me.teamId ? myGame.homeWinProb : 100 - myGame.homeWinProb)
    : null

  const situationLine = myGame && myGame.state === 'in' && myGame.situation
    ? [myGame.situation.possessionText, myGame.situation.downDistanceText].filter(Boolean).join(' · ')
    : null

  const eyebrowOnFill: CSSProperties = {
    fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em',
    textTransform: 'uppercase', color: inkSoft,
  }

  const scoreBlock = (score: number | null, abbr: string, opponentSide: boolean) => (
    <div style={{ textAlign: 'center', opacity: opponentSide ? 0.78 : 1, minWidth: 0 }}>
      <div className="numerals" style={{ fontSize: 60, fontWeight: 800, lineHeight: 1 }}>
        {score ?? '-'}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.1em', marginTop: 6 }}>{abbr}</div>
    </div>
  )

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <HomeStyles />

      {/* 1 — the filled score header */}
      <Header
        filled
        left={<div style={eyebrowOnFill}>Game day</div>}
        right={
          <Freshness asOf={state.scoresAsOf} health={state.feedHealth} onFill />
        }
        sub={
          myGame ? (
            <div aria-live="polite">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 18 }}>
                {scoreBlock(us, teamAbbr(me.teamId), false)}
                <div className="mono" style={{ fontSize: 12, textAlign: 'center', color: inkFaint }}>
                  <div>{gameClock(myGame)}</div>
                  {myWinPct !== null ? <div style={{ marginTop: 4 }}>{myWinPct}% win</div> : null}
                </div>
                {scoreBlock(them, teamAbbr(oppId), true)}
              </div>
              {situationLine ? (
                <div style={{ marginTop: 18, fontSize: 13, textAlign: 'center', color: inkFaint }}>
                  {situationLine}
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ marginTop: 14, fontSize: 15, fontWeight: 700 }}>
              {state.games.filter((g) => g.state === 'in').length
                ? `${state.games.filter((g) => g.state === 'in').length} games on now`
                : 'Football today'}
            </div>
          )
        }
      />

      <Body style={{ padding: '18px var(--gutter) calc(24px + var(--nav-h))' }}>
        {error ? (
          <>
            <Notice tone="warn">{error}. Showing the last update.</Notice>
            <Spacer h={16} />
          </>
        ) : null}

        <div className="sfg-home-cols">
          {/* 2 — who is here */}
          {headcount > 0 || present.length > 0 ? (
            <section key="room">
              <h2 className="big-title" style={{ margin: 0 }}>{headcount} here today</h2>
              <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
                {state.roster.fansOfMyTeam} {nickname} {state.roster.fansOfMyTeam === 1 ? 'fan' : 'fans'} in the room
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                {present.map((p) => (
                  <Avatar key={p.id} initials={p.initials || initialsOf(p.displayName)} self={p.id === me.id} />
                ))}
                {overflow > 0 ? (
                  <Avatar initials={`+${overflow}`} style={{ fontSize: 12, color: 'var(--dim)' }} />
                ) : null}
              </div>
            </section>
          ) : null}

          {/* 3 — around the league */}
          {league.length ? (
            <section key="league" style={{ marginTop: CARD_GAP }}>
              <Eyebrow>Around the league</Eyebrow>
              <div style={{ marginTop: 10 }}>
                {league.map((g, i) => (
                  <Link
                    key={g.id}
                    to={`/game/${g.id}`}
                    style={{
                      ...monoRow,
                      color: 'var(--fg)',
                      borderBottom: i === league.length - 1 ? '1px solid var(--line)' : undefined,
                      minHeight: 44,
                    }}
                  >
                    <span style={{ color: 'var(--dim)' }}>
                      {teamAbbr(g.awayTeamId)} @ {teamAbbr(g.homeTeamId)}
                    </span>
                    <span style={{ color: g.state === 'pre' ? 'var(--dim)' : 'var(--fg)', flex: '0 0 auto' }}>
                      {g.state === 'pre'
                        ? gameClock(g)
                        : `${g.awayScore ?? 0} · ${g.homeScore ?? 0}  ${gameClock(g)}`}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          {/* 4 — what this room cares about */}
          {favorites.length ? (
            <section key="favorites" style={{ marginTop: CARD_GAP }}>
              <Eyebrow large>What this room cares about</Eyebrow>
              <div style={{ marginTop: 10 }}>
                {favorites.map((f, i) => (
                  <Link
                    key={f.gameId}
                    to={`/game/${f.gameId}`}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                      padding: '12px 0', borderBottom: '1px solid var(--line)', minHeight: 44,
                      color: 'var(--fg)',
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, minWidth: 0 }}>{i + 1}. {f.label}</span>
                    <span style={{ fontSize: 12, color: 'var(--dim)', flex: '0 0 auto', textAlign: 'right' }}>
                      {f.reason}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          {/* 5 — chip in */}
          {fund ? (
            <Card key="chipin" style={{ marginTop: CARD_GAP, padding: 18, borderRadius: 'var(--radius-card)' }}>
              <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>Chip in for today</div>
              <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 5 }}>
                Suggested {money(fund.suggestedC)} · {money(fund.confirmedC)} of {money(fund.targetC)} in
              </div>
              <Button
                variant="outline-bright"
                onClick={() => navigate('/fund')}
                style={{ marginTop: 14, borderRadius: 14 }}
              >
                Chip in {money(fund.suggestedC)}
              </Button>
            </Card>
          ) : null}

          {/* 6 — fund strip. Same component the planning screen uses. */}
          <FundStrip key="fundstrip" style={{ marginTop: 20 }} />

          {/* 7 — live challenges */}
          {live.length ? (
            <Card key="live" style={{ marginTop: CARD_GAP, padding: 18, borderRadius: 'var(--radius-card)' }}>
              <Eyebrow large style={{ marginBottom: 12 }}>Live challenges</Eyebrow>
              {live.map((c, i) => (
                <div key={c.id}>
                  {i > 0 ? <Divider style={{ margin: '14px 0' }} /> : null}
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {c.resolutionSrc === 'auto' ? '⚡ ' : ''}
                    {mySideName(c)} vs {otherSideName(c)} · {c.stakeLabel}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 3 }}>{liveSub(c)}</div>
                </div>
              ))}
              <Button compact onClick={() => navigate('/challenges')} style={{ marginTop: 14, fontWeight: 600 }}>
                View
              </Button>
            </Card>
          ) : null}

          {/* 8 — today in this room */}
          {feed.length ? (
            <section key="feed" style={{ marginTop: 26 }}>
              <Eyebrow large>Today in this room</Eyebrow>
              <div style={{ marginTop: 4 }} aria-live="polite">
                {feed.map((e) => <FeedRow key={e.id} event={e} />)}
              </div>
            </section>
          ) : null}

          {/* 9 — account business and the shout prompt, same as the
              planning home: neither depends on whether it is Sunday. */}
          <ConfirmEmailCard />
          <HomeShout />

          {/* 10 — our valued sponsors */}
          <AdSlot />
        </div>
      </Body>
    </Screen>
  )
}

function liveSub(c: Challenge): string {
  const quoted = `“${c.proposition}”`
  if (c.status === 'resolved' || c.status === 'settled') {
    if (c.viewer.won === true) return `${quoted}. You win`
    if (c.viewer.won === false) return `${quoted}. ${otherSideName(c)} wins`
    return `${quoted}. Resolved`
  }
  if (c.isLocked) return `${quoted} · locked`
  return c.gameLabel ? `${quoted} · ${c.gameLabel}` : quoted
}

function FeedRow({ event }: { event: FeedEvent }) {
  const body = (
    <>
      <div className="mono" style={{ fontSize: 10, color: 'var(--dim)', width: 40, flex: '0 0 auto', paddingTop: 3 }}>
        {bareTime(event.at)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: event.highlight ? 'var(--bright)' : 'var(--fg)' }}>
          {event.emoji ? `${event.emoji} ` : ''}{event.title}
        </div>
        {event.detail ? (
          <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 3, lineHeight: 1.4 }}>{event.detail}</div>
        ) : null}
      </div>
    </>
  )

  const style: CSSProperties = {
    display: 'flex', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--line)',
    color: 'var(--fg)', minHeight: 44,
  }

  return event.href
    ? <Link to={event.href} style={style}>{body}</Link>
    : <div style={style}>{body}</div>
}

// ════════════════════════════════════════════════════════════════════════════
// Normal mode (screen 3a)
// ════════════════════════════════════════════════════════════════════════════

function NormalHome() {
  const { state, error } = useStore()
  const navigate = useNavigate()
  if (!state) return null

  const me = state.user
  const week = state.week
  const needsYou = state.challenges.needsYou

  const upcoming = state.games.filter((g) => g.state !== 'post')
  // The viewer's own game gets the card; the strip below is everything else,
  // because showing it twice makes the card look like a duplicate rather than
  // a headline.
  const myGame = upcoming.find((g) => g.homeTeamId === me.teamId || g.awayTeamId === me.teamId) ?? null
  const nextGames = upcoming.filter((g) => g.id !== myGame?.id).slice(0, 8)

  // D26 — offseason: no week at all, or a week with nothing left to play.
  const offseason = !week || (state.games.length === 0)

  // Answered questions leave the home screen. Both live on in the bottom bar,
  // which moves them to the overflow menu rather than dropping them, so
  // changing your mind is two taps instead of impossible. Leaving an answered
  // prompt on Home was the same mistake as "Host: not set": a screen that keeps
  // asking after you have replied.
  const askingToCheckIn = !offseason && (state.attendance?.mine ?? null) === null
  const askingToChipIn = !!state.fund && !state.fund.iContributed

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <HomeStyles />
      <Header
        bordered={false}
        wordmark
        right={
          <Link to="/settings" aria-label="Your profile and settings" style={{ display: 'block', minHeight: 44, paddingTop: 5 }}>
            <Avatar initials={initialsOf(me.displayName)} size={34} self />
          </Link>
        }
      />

      <Body style={{ padding: '6px var(--gutter) calc(24px + var(--nav-h))' }}>
        {error ? (
          <>
            <Notice tone="warn">{error}. Showing the last update.</Notice>
            <Spacer h={12} />
          </>
        ) : null}

        <div className="sfg-home-cols">
          {offseason ? (
            <EmptyState
              key="offseason"
              title="No football this week."
              detail="Attendance opens again when the next slate lands. Season-long challenges are still live."
              actionLabel="See challenges"
              onAction={() => navigate('/challenges')}
              variant="secondary"
            />
          ) : askingToCheckIn ? (
            <CheckInCard key="attendance" />
          ) : null}

          {/* Directly under "Are you coming?" — the two questions a week asks
              are the same question twice, and they belong next to each other. */}
          {askingToChipIn ? <FundCard key="fund" style={{ marginTop: 18 }} /> : null}

          {myGame ? (
            <MatchupCard
              key="mygame"
              game={myGame}
              myTeamId={me.teamId}
              eyebrow={week ? `Your game · ${week.label}` : 'Your game'}
              to={`/game/${myGame.id}`}
              style={{ marginTop: 18 }}
            />
          ) : null}

          {nextGames.length ? (
            <section key="next" style={{ marginTop: 20 }}>
              <Eyebrow>Next games</Eyebrow>
              <div
                className="scr"
                style={{ display: 'flex', gap: 10, marginTop: 10, overflowX: 'auto', overflowY: 'hidden', paddingBottom: 2 }}
              >
                {nextGames.map((g) => {
                  const mine = g.homeTeamId === me.teamId || g.awayTeamId === me.teamId
                  const spread = spreadLabel(g)
                  return (
                    <Link
                      key={g.id}
                      to={`/game/${g.id}`}
                      style={{
                        flex: '0 0 auto', background: 'var(--surf)',
                        border: `1px solid ${mine ? 'var(--fill)' : 'var(--line)'}`,
                        borderRadius: 14, padding: '12px 14px', color: 'var(--fg)', minHeight: 44,
                      }}
                    >
                      <div className="mono" style={{ fontSize: 13, fontWeight: 600 }}>
                        {teamAbbr(g.awayTeamId)} @ {teamAbbr(g.homeTeamId)}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 5 }}>{shortDayTime(g.kickoffAt)}</div>
                      {spread ? (
                        <div
                          className="mono"
                          style={{ fontSize: 11, marginTop: 4, color: mine ? 'var(--bright)' : 'var(--dim)' }}
                        >
                          {spread}
                        </div>
                      ) : null}
                    </Link>
                  )
                })}
              </div>
            </section>
          ) : null}

          {/* Below Next games, and on every day rather than only game day. */}
          <FundStrip key="fundstrip" style={{ marginTop: 20 }} />

          <HomeTimeline key="timeline" />

          {needsYou.length ? <ChallengesNeedYou key="needsyou" challenges={needsYou} /> : null}

          <SeasonStandings key="standings" season={week?.seasonYear} style={{ marginTop: 18 }} />

          <ConfirmEmailCard key="confirm" />

          <HomePicks key="picks" />

          <HomeShout key="shout" />

          {/* The foot of the screen, deliberately: it answers nothing the week
              needs from you, it is just nice to know. */}
          <RootingTable key="rooting" style={{ marginTop: 24 }} />

          {/* Our valued sponsors. Both Home variants get one — game day and
              planning are two different screens and neither is "the" home. */}
          <AdSlot />
        </div>
      </Body>
    </Screen>
  )
}

// ── the timeline module (D121) ──────────────────────────────────────────────

/**
 * The seven most recent things, and a door to the rest.
 *
 * The rows come down inside `/api/state` rather than from a second fetch, so
 * the module is present on first paint instead of popping in a beat later under
 * a screen people scroll immediately.
 */
function HomeTimeline() {
  const { state } = useStore()
  const events = state?.timeline ?? []
  if (!events.length) return null

  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <Eyebrow large>This week</Eyebrow>
        <Link to="/timeline" style={{ fontSize: 12, fontWeight: 700, flex: '0 0 auto' }}>
          Timeline →
        </Link>
      </div>
      <div style={{ marginTop: 6 }}>
        <TimelineList events={events} now={state?.now ?? Date.now()} />
      </div>
    </section>
  )
}

// ── confirm your email ──────────────────────────────────────────────────────

/**
 * The one piece of unfinished account business worth interrupting for.
 *
 * Until an address is confirmed, the username is walk-in: anyone who types it
 * on the login screen is offered the account. That is a deliberate trade made
 * at signup so nobody is blocked from joining, and this box is the other half
 * of it — the standing offer to close the door, stated plainly enough that the
 * trade is visible.
 *
 * Deliberately not persisted. Reload without confirming and the form is blank
 * again, because a form that remembers a half-finished attempt implies progress
 * that does not exist: the only thing that counts is the tap in the email.
 */
function ConfirmEmailCard() {
  const { state } = useStore()
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const send = useCallback(async (address: string) => {
    setBusy(true)
    setErr(null)
    try {
      const res = await POST<{ maskedEmail: string }>('/api/auth/verify/start', { email: address })
      setSentTo(res.maskedEmail)
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t send that. Try again.')
    } finally {
      setBusy(false)
    }
  }, [])

  if (!state || state.user.isVerified) return null

  return (
    <Card style={{ marginTop: 18 }}>
      <div style={{ fontSize: 16, fontWeight: 700 }}>Enter your email to confirm your account.</div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
        Anyone can login using your username until you confirm your email address
      </div>

      {sentTo ? (
        <>
          <Spacer h={14} />
          <Notice tone="info">
            Check your inbox for a login link. Look in your spam folder too, it likes to hide there.
          </Notice>
          <Spacer h={12} />
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => { void send(email.trim().toLowerCase()) }}
          >
            {busy ? 'Sending…' : 'Send again'}
          </Button>
        </>
      ) : (
        <>
          <Spacer h={14} />
          <Field
            type="email"
            inputMode="email"
            autoComplete="email"
            aria-label="Your email address"
            placeholder="you@example.com"
            value={email}
            error={err}
            onChange={(e) => { setEmail(e.target.value); setErr(null) }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && email.includes('@')) void send(email.trim().toLowerCase())
            }}
          />
          <Spacer h={12} />
          <Button
            disabled={busy || !email.includes('@')}
            onClick={() => { void send(email.trim().toLowerCase()) }}
          >
            {busy ? 'Sending…' : 'Confirm my email'}
          </Button>
        </>
      )}
    </Card>
  )
}

// ── picks, takeable here ────────────────────────────────────────────────────

/**
 * The counter, plus up to three picks you can make without leaving.
 *
 * The three are the games that lock soonest, which is the only ordering that
 * makes a three-item subset useful. Each tap is a full save (D78 — there is no
 * submit step anywhere in pick'em), and the counter above moves with it: the
 * response carries the new totals and they go straight into the shared state,
 * so the bottom bar's dot and this line agree without a refetch.
 */
function HomePicks() {
  const { state, patch, toast } = useStore()
  const navigate = useNavigate()
  const [busy, setBusy] = useState<string | null>(null)
  const [done, setDone] = useState<Record<string, string>>({})

  const pick = useCallback(async (gameId: string, teamId: string) => {
    setBusy(gameId)
    setDone((d) => ({ ...d, [gameId]: teamId }))
    try {
      const res = await PUT<{ made: number; total: number }>(
        `/api/picks/${encodeURIComponent(gameId)}`, { teamId },
      )
      patch((prev) => ({
        ...prev,
        picks: {
          ...prev.picks,
          made: res.made,
          total: res.total,
          // Drop it from the offered three; the next refresh backfills another.
          outstanding: prev.picks.outstanding.filter((o) => o.game.id !== gameId),
        },
      }))
    } catch (e) {
      setDone((d) => { const next = { ...d }; delete next[gameId]; return next })
      toast(e instanceof ApiFailure ? e.message : 'That pick didn’t save.')
    } finally {
      setBusy(null)
    }
  }, [patch, toast])

  if (!state || state.picks.total === 0) return null
  const { made, total, outstanding } = state.picks
  if (made >= total) return null

  return (
    <Card style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            🎯 You’ve picked {made} of {total} games
          </div>
          <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 4 }}>Picks lock at each kickoff</div>
        </div>
        <button
          onClick={() => navigate('/picks/edit')}
          style={{
            flex: '0 0 auto', fontSize: 12, fontWeight: 700, padding: '9px 12px', borderRadius: 999,
            background: 'var(--fill)', color: 'var(--ink)', minHeight: 44,
          }}
        >
          Finish
        </button>
      </div>

      {outstanding.length ? (
        <div style={{ marginTop: 14 }}>
          {outstanding.map((pg) => {
            const g = pg.game
            const chosen = done[g.id] ?? null
            return (
              <div key={g.id} style={{ marginTop: 12 }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--dim)' }}>
                  {shortDayTime(g.kickoffAt)}
                </div>
                <div
                  role="group"
                  aria-label={`${teamAbbr(g.awayTeamId)} at ${teamAbbr(g.homeTeamId)}`}
                  style={{ display: 'flex', gap: 8, marginTop: 6, opacity: busy === g.id ? 0.7 : 1 }}
                >
                  {[g.awayTeamId, g.homeTeamId].map((teamId) => {
                    const on = chosen === teamId
                    return (
                      <button
                        key={teamId}
                        onClick={() => { if (!on) void pick(g.id, teamId) }}
                        aria-pressed={on}
                        aria-label={`Pick ${teamAbbr(teamId)}`}
                        style={{
                          flex: 1, textAlign: 'center', padding: 13, minHeight: 48, borderRadius: 12,
                          fontSize: 14, fontWeight: on ? 700 : 600,
                          background: on ? 'var(--fill)' : 'transparent',
                          color: on ? 'var(--ink)' : 'var(--fg)',
                          border: on ? '1px solid var(--fill)' : '1px solid var(--outline)',
                        }}
                      >
                        {teamAbbr(teamId)}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </Card>
  )
}

// ── the shout module ────────────────────────────────────────────────────────

/**
 * A prompt, not a text field.
 *
 * It looks like an input and opens the composer, because the composer is where
 * the week picker, the @ menu and the image live. Two ways to write a shout
 * would mean two sets of rules about all three.
 */
function HomeShout() {
  const { state } = useStore()
  const [open, setOpen] = useState(false)
  if (!state || state.user.optedOut) return null

  return (
    <section style={{ marginTop: 22 }}>
      {/*
        The heading says what the module is; the question is the invitation.
        They were the same line before, which meant the only thing naming this
        section was a question — and a question makes a poor label when you are
        scanning eleven modules for the one you want.
      */}
      <Eyebrow large>Add a shout</Eyebrow>
      <div style={{ fontSize: 14, color: 'var(--dim)', marginTop: 6 }}>
        What’s on your mind?
      </div>
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          width: '100%', textAlign: 'left', marginTop: 10,
          padding: '15px 16px', minHeight: 48, borderRadius: 'var(--radius-btn)',
          border: '1px solid var(--outline)', background: 'var(--surf)',
          color: 'var(--dim)', fontSize: 15, lineHeight: 1.4,
        }}
      >
        <span style={{ fontSize: 20, lineHeight: 1, flex: '0 0 auto' }} aria-hidden>📣</span>
        <span style={{ minWidth: 0 }}>
          We’re counting on you to heighten the intellectual discourse.
        </span>
      </button>
      <ShoutSheet open={open} onClose={() => setOpen(false)} />
    </section>
  )
}

// ── inline challenge actions ────────────────────────────────────────────────

function ChallengesNeedYou({ challenges }: { challenges: Challenge[] }) {
  const { refresh, toast } = useStore()
  const [busyId, setBusyId] = useState<string | null>(null)

  const act = useCallback(
    async (id: string, action: 'accept' | 'decline' | 'confirm' | 'dispute') => {
      setBusyId(id)
      try {
        const res = await writeOrQueue(
          `/api/challenges/${id}/${action}`,
          'POST',
          action === 'dispute' ? { note: null } : {},
          action,
        )
        if (res === null) toast('Saved. It will send when you’re back online')
        else await refresh(true)
      } catch (e) {
        toast(e instanceof ApiFailure ? e.message : 'That didn’t go through.')
      } finally {
        setBusyId(null)
      }
    },
    [refresh, toast],
  )

  const n = challenges.length

  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>
        🤝 {n} {n === 1 ? 'challenge needs' : 'challenges need'} you
      </div>
      {challenges.map((c, i) => {
        const confirming = c.viewer.needsConfirm
        const title = confirming
          ? c.viewer.won === true
            ? `Resolved: you won vs ${otherSideName(c)}`
            : c.viewer.won === false
              ? `Resolved: ${otherSideName(c)} won`
              : `Resolved: ${c.proposition}`
          : `${c.creatorName} → you: ${c.proposition}`
        return (
          <div key={c.id}>
            {i > 0 ? <Divider style={{ margin: '14px 0' }} /> : null}
            <div style={{ marginTop: i === 0 ? 12 : 0, fontSize: 14 }}>{title}</div>
            <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 6 }}>{c.stakeLabel}</div>
            <ButtonPair
              primaryLabel={confirming ? 'Confirm' : 'Accept'}
              secondaryLabel={confirming ? 'Dispute' : 'Decline'}
              busy={busyId === c.id}
              onPrimary={() => void act(c.id, confirming ? 'confirm' : 'accept')}
              onSecondary={() => void act(c.id, confirming ? 'dispute' : 'decline')}
            />
          </div>
        )
      })}
    </Card>
  )
}
